/* global Map */

/**
 * Provide a class that creates a custom event.
 *
 * Note: this module depends on src/component/global/polyfill being loaded
 *
 * @module component/util/event
 */

// Local imports
import * as enumtool from '../util/enumtool.js';

function is_enum(event_kind) {
    if (typeof event_kind !== 'object') {
        return false;
    }
    let count = 0;
    for (let variant of Object.values(event_kind)) {
        if (typeof variant !== 'symbol') {
            return false;
        }
        count++;
    }
    return count > 0;
}

function event_type_error(event_builder, event) {
    if (!event_builder.is_event(event)) {
        let errmsg = `Expected event, got ${typeof event} instead`;
        throw new Error(errmsg);
    }
}

/**
 * Listen for a custom event.
 *
 * This will create a listener for a custom event. Once the specified custom
 * event is triggered, then the specified callback function will be called.
 *
 * @param {Window} global
 * The global object (window in browser context)
 *
 * @param {Object.<string, Symbol>} event_kind
 * An object representing an enum. An enum is defined as an object whose
 * attributes map a string in PascalCase as the attribute name and a Symbol
 * object wrapping a string that identifies a unique event name.
 *
 * @param {Symbol} event_type
 * An object that is an attribute value of the object specified by the
 * event_kind parameter. If an object is passed in here that is not a member of
 * the event_kind object, an exception will be raised.
 *
 * @param {function} cb
 * A callback that is run when the specified event is triggered. This callback
 * may optionally accept a single parameter which will be the event object that
 * was triggered.
 *
 * @example
 * const AnswerKind = {
 *    Life: Symbol('event.answer.life'),
 * }
 *
 * add_listener(global, AnswerKind, AnswerKind.Life, event => {
 *     event.detail.answer = 42;
 * })
 *
 */
export function add_listener(global, event_kind, event_type, cb) {
    let eb = new EventBuilder(global, event_kind);
    eb.add_listener(event_type, cb);
}

/**
 * Class that builds a new custom event object.
 *
 * @example
 * const AnswerKind = {
 *    Life: Symbol('event.answer.life'),
 * }
 *
 * let builder = new EventBuilder(window, AnswerKind)
 * let event = builder.create(AnswerKind.Life)
 *
 */
export class EventBuilder {
    /**
     * Create a new custom event builder.
     *
     * @param {Window} global
     * The global object (window in browser context)
     *
     * @param {Object.<string, Symbol>} event_kind
     * An object representing an enum. An enum is defined as an object whose
     * attributes map a string in PascalCase as the attribute name and a Symbol
     * object wrapping a string that identifies a unique event name.
     *
     */
    constructor(global, event_kind) {
        if (!is_enum(event_kind)) {
            let ek_type = typeof event_kind;
            let errmsg = `Expected enum-type object, got ${ek_type} instead`;
            throw new Error(errmsg);
        }

        this.global = global;
        this.event_kind = event_kind;
    }

    /**
     * Determine if the given object is a variant of the enum used to initialize
     * the builder.
     *
     * @param {Object.<string, Symbol>} event
     * An object representing an enum. An enum is defined as an object whose
     * attributes map a string in PascalCase as the attribute name and a Symbol
     * object wrapping a string that identifies a unique event name.
     *
     * @example
     * const AnswerKind = {
     *    Life: Symbol('event.answer.life'),
     * }
     *
     * let builder = new EventBuilder(window, AnswerKind)
     * builder.is_event(42) //returns false
     * builder.is_event(AnswerKind.Life) // returns true
     *
     */
    is_event(event) {
        for (let variant of Object.values(this.event_kind)) {
            if (event === variant) {
                return true;
            }
        }
        return false;
    }

    /**
     * Create a custom event object from the given event kind variant.
     *
     * Note: the event object passed in must evaluate to true when passed into
     * the
     * [EventBuilder.is_event()]{@link module:component/util/event.EventBuilder#is_event}
     * method.
     *
     * @param {Object.<string, Symbol>} event
     * An object representing an enum. An enum is defined as an object whose
     * attributes map a string in PascalCase as the attribute name and a Symbol
     * object wrapping a string that identifies a unique event name.
     *
     * @example
     * const AnswerKind = {
     *    Life: Symbol('event.answer.life'),
     * }
     *
     * let builder = new EventBuilder(window, AnswerKind)
     * let event = builder.create(AnswerKind.Life)
     *
     */
    create(event) {
        event_type_error(this, event);
        return new Event(this.global, event);
    }

    /**
     * Create a map containing custom event objects of all variants of the enum
     * used to initialize the builder.
     *
     * @example
     * const AnswerKind = {
     *    Life: Symbol('event.answer.life'),
     *    Universe: Symbol('event.answer.universe'),
     *    Everything: Symbol('event.answer.everything'),
     * }
     *
     * let builder = new EventBuilder(window, AnswerKind)
     * let event_map = builder.create_all()
     *
     * event_map.length // returns 3
     *
     * // returns the event created from this variant
     * event_map.get(AnswerKind.Universe)
     *
     */
    create_all() {
        let event_map = new Map();
        for (let variant of Object.values(this.event_kind)) {
            let event = this.create(variant);
            event_map.set(variant, event);
        }
        return event_map;
    }

    /**
     * Listen for a custom event.
     *
     * This will create a listener for a custom event. Once the specified custom
     * event is triggered, then the specified callback function will be called.
     *
     * This method will create an event object out of the given enum variant and
     * use that to create a listener for the event specified by the given enum
     * variant. This is intended to be used when it is not desired to hold on to
     * the event object itself but to only create a listener.
     *
     * @param {Symbol} event
     * An object that is an attribute value of the object specified by the
     * event_kind parameter. If an object is passed in here that is not a member of
     * the event_kind object, an exception will be raised.
     *
     * @param {function} cb
     * A callback that is run when the specified event is triggered. This callback
     * may optionally accept a single parameter which will be the event object that
     * was triggered.
     *
     * @example
     * const AnswerKind = {
     *    Life: Symbol('event.answer.life'),
     * }
     *
     * let builder = new EventBuilder(window, AnswerKind)
     * builder.add_listener(AnswerKind.Life, event => {
     *   event.detail.answer = 42
     * })
     *
     */
    add_listener(event, cb) {
        let event_obj = this.create(event);
        event_obj.add_listener(cb);
    }

    /**
     * Read-only attribute that returns the enum object used to initialize the
     * builder.
     *
     * An enum is defined as an object whose attributes map a string in
     * PascalCase as the attribute name and a Symbol object wrapping a string
     * that identifies a unique event name.
     *
     * @type {Object.<string, Symbol>}
     *
     * @example
     * const AnswerKind = {
     *    Life: Symbol('event.answer.life'),
     * }
     *
     * let builder = new EventBuilder(window, AnswerKind)
     *
     * // returns AnswerKind
     * let builder_enum = builder.kind
     *
     */
    get kind() {
        return this.event_kind;
    }
}

// ===========================================================================
// Event
// ===========================================================================

// This assumes event exists in builder
function create_event(event, detail) {
    let data = undefined;
    if (detail) {
        data = { detail };
    }
    let event_type = enumtool.enum_value(event);
    return new CustomEvent(event_type, data);
}

/**
 * Class that represents a custom event.
 *
 * This can only be created by using the
 * [EventBuilder]{@link module:component/util/event.EventBuilder} class.
 *
 * @example
 * const AnswerKind = {
 *    Life: Symbol('event.answer.life'),
 * }
 *
 * // Create a new event
 * let builder = new EventBuilder(window, AnswerKind)
 * let event = builder.create(AnswerKind.Life)
 *
 * let answer = 0
 *
 * // Set the value of answer to whatever value is set by the event
 * event.add_listener(event => {
 *    answer = event.detail.value
 * })
 *
 * // This will set the value of the answer variable to 42
 * event.trigger({ value: 42 })
 *
 */
class Event {
    constructor(global, event) {
        this.global = global;
        this.event = event;
    }

    /**
     * Trigger this event with the given detail object
     *
     * Note: this will dispatch the event on window.document
     *
     * @param {Object} [detail]
     * Any object. If not given, then the CustomEvent that is created will not
     * have a detail attribute.
     *
     * The detail attribute is used to pass information from the event to any
     * listeners.
     *
     * @example
     * const AnswerKind = {
     *    Life: Symbol('event.answer.life'),
     * }
     *
     * // Create a new event
     * let builder = new EventBuilder(window, AnswerKind)
     * let event = builder.create(AnswerKind.Life)
     *
     * // Trigger the event
     * event.trigger({ answer: 42 })
     *
     */
    trigger(detail) {
        let e = create_event(this.event, detail);
        this.global.document.dispatchEvent(e);
    }

    /**
     * Listen for the custom event.
     *
     * This will create a listener for the custom event. Once the specified
     * custom event is triggered, then the specified callback function will be
     * called.
     *
     * @param {function} cb
     * A callback that is run when the specified event is triggered. This callback
     * may optionally accept a single parameter which will be the event object that
     * was triggered.
     *
     * @example
     * const AnswerKind = {
     *    Life: Symbol('event.answer.life'),
     * }
     *
     *  // Create an event
     * let builder = new EventBuilder(window, AnswerKind)
     * let event = builder.create(AnswerKind.Life)
     *
     * let answer = 0
     *
     * builder.add_listener(AnswerKind.Life, event => {
     *   answer = event.detail.value
     * })
     *
     */
    add_listener(cb) {
        this.global.document.addEventListener(this.type, cb);
    }

    /**
     * Read-only attribute that returns the enum variant object used to
     * initialize the event.
     *
     * @type {Symbol}
     *
     * @example
     * const AnswerKind = {
     *    Life: Symbol('event.answer.life'),
     * }
     *
     *  // Create event
     * let builder = new EventBuilder(window, AnswerKind)
     * let event = builder.create(AnswerKind.Life)
     *
     * // returns AnswerKind.Life
     * let kind = event.type
     *
     */
    get type() {
        return enumtool.enum_value(this.event);
    }
}

// ===========================================================================
//
// ===========================================================================
